﻿(function (window, document) {
  'use strict';

  if (window.TalonHomepagePerformance) return;

  var loaderScript = document.currentScript;
  var settings = {
    designMode: Boolean(loaderScript && loaderScript.dataset.designMode === 'true'),
    swiperSrc: loaderScript ? loaderScript.dataset.swiperSrc : '',
    swiperStyle: loaderScript ? loaderScript.dataset.swiperStyle : '',
    stampedSrc: loaderScript ? loaderScript.dataset.stampedSrc : '',
    stampedApiKey: loaderScript ? loaderScript.dataset.stampedApiKey : '',
    stampedStoreId: loaderScript ? loaderScript.dataset.stampedStoreId : '',
  };
  var resourcePromises = Object.create(null);
  var registrations = Object.create(null);
  var stampedInitialized = false;

  function noop() {}

  function reportError(label, error) {
    if (settings.designMode && window.console && typeof window.console.warn === 'function') {
      window.console.warn('[Talon performance] ' + label, error);
    }
  }

  function applyAttributes(element, attributes) {
    if (!attributes) return;

    Object.keys(attributes).forEach(function (name) {
      var value = attributes[name];
      if (value === false || value === null || typeof value === 'undefined') return;
      if (value === true) {
        element.setAttribute(name, '');
      } else {
        element.setAttribute(name, value);
      }
    });
  }

  function loadScriptOnce(key, src, attributes) {
    if (!src) return Promise.reject(new Error('Missing script URL for ' + key));
    if (resourcePromises[key]) return resourcePromises[key];

    resourcePromises[key] = new Promise(function (resolve, reject) {
      var selector = 'script[data-talon-resource="' + key + '"]';
      var script = document.querySelector(selector);

      function handleLoad() {
        script.dataset.talonLoaded = 'true';
        resolve(script);
      }

      function handleError() {
        delete resourcePromises[key];
        reject(new Error('Unable to load ' + src));
      }

      if (script) {
        if (script.dataset.talonLoaded === 'true' || script.readyState === 'complete') {
          resolve(script);
          return;
        }
        script.addEventListener('load', handleLoad, { once: true });
        script.addEventListener('error', handleError, { once: true });
        return;
      }

      script = document.createElement('script');
      script.src = src;
      script.dataset.talonResource = key;
      applyAttributes(script, attributes);
      script.addEventListener('load', handleLoad, { once: true });
      script.addEventListener('error', handleError, { once: true });
      document.head.appendChild(script);
    });

    return resourcePromises[key];
  }

  function loadStyleOnce(key, href) {
    if (!href) return Promise.reject(new Error('Missing stylesheet URL for ' + key));
    if (resourcePromises[key]) return resourcePromises[key];

    resourcePromises[key] = new Promise(function (resolve, reject) {
      var selector = 'link[data-talon-resource="' + key + '"]';
      var link = document.querySelector(selector);
      if (!link) {
        link = Array.prototype.find.call(document.querySelectorAll('link[rel="stylesheet"]'), function (item) {
          return item.href === href;
        });
      }

      function handleLoad() {
        link.dataset.talonLoaded = 'true';
        resolve(link);
      }

      function handleError() {
        delete resourcePromises[key];
        reject(new Error('Unable to load ' + href));
      }

      if (link) {
        if (link.dataset.talonLoaded === 'true' || link.sheet) {
          resolve(link);
          return;
        }
        link.addEventListener('load', handleLoad, { once: true });
        link.addEventListener('error', handleError, { once: true });
        return;
      }

      link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.dataset.talonResource = key;
      link.addEventListener('load', handleLoad, { once: true });
      link.addEventListener('error', handleError, { once: true });
      document.head.appendChild(link);
    });

    return resourcePromises[key];
  }

  function onIdle(callback, timeout) {
    var cancelled = false;
    var idleId;
    var timerId;

    function run() {
      if (cancelled) return;
      if (typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(
          function () {
            if (!cancelled) callback();
          },
          { timeout: timeout || 2000 }
        );
      } else {
        timerId = window.setTimeout(function () {
          if (!cancelled) callback();
        }, Math.min(timeout || 2000, 500));
      }
    }

    if (document.readyState === 'complete') {
      run();
    } else {
      window.addEventListener('load', run, { once: true });
    }

    return function () {
      cancelled = true;
      window.removeEventListener('load', run);
      if (typeof window.cancelIdleCallback === 'function' && idleId) {
        window.cancelIdleCallback(idleId);
      }
      if (timerId) window.clearTimeout(timerId);
    };
  }

  function whenVisible(element, callback, options) {
    if (!element || typeof callback !== 'function') return noop;

    var finished = false;
    var observer;
    var cancelFallback = noop;
    var observerOptions = options || {};

    function cleanup() {
      if (observer) observer.disconnect();
      cancelFallback();
      element.removeEventListener('pointerdown', trigger, true);
      element.removeEventListener('touchstart', trigger, true);
      element.removeEventListener('focusin', trigger, true);
    }

    function trigger() {
      if (finished) return;
      finished = true;
      cleanup();

      try {
        Promise.resolve(callback(element)).catch(function (error) {
          reportError('Deferred section failed:', error);
        });
      } catch (error) {
        reportError('Deferred section failed:', error);
      }
    }

    if (settings.designMode || observerOptions.immediate) {
      trigger();
      return cleanup;
    }

    element.addEventListener('pointerdown', trigger, { capture: true, passive: true });
    element.addEventListener('touchstart', trigger, { capture: true, passive: true });
    element.addEventListener('focusin', trigger, true);

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        function (entries) {
          if (entries.some(function (entry) { return entry.isIntersecting; })) trigger();
        },
        {
          rootMargin: observerOptions.rootMargin || '600px 0px',
          threshold: typeof observerOptions.threshold === 'number' ? observerOptions.threshold : 0.01,
        }
      );
      observer.observe(element);
    } else {
      cancelFallback = onIdle(trigger, observerOptions.fallbackTimeout || 1200);
    }

    return cleanup;
  }

  function loadSwiper() {
    if (window.Swiper) return Promise.resolve(window.Swiper);

    var stylePromise = settings.swiperStyle
      ? loadStyleOnce('swiper-style', settings.swiperStyle).catch(function (error) {
          reportError('Swiper stylesheet failed:', error);
        })
      : Promise.resolve();

    return stylePromise
      .then(function () {
        return loadScriptOnce('swiper-script', settings.swiperSrc, { defer: true });
      })
      .then(function () {
        if (!window.Swiper) throw new Error('Swiper loaded without exposing window.Swiper');
        return window.Swiper;
      });
  }

  function loadStamped() {
    if (window.StampedFn) {
      initializeStamped();
      return Promise.resolve(window.StampedFn);
    }

    return loadScriptOnce('stamped-sdk', settings.stampedSrc, { async: true }).then(function () {
      if (!window.StampedFn) throw new Error('Stamped loaded without exposing window.StampedFn');
      initializeStamped();
      return window.StampedFn;
    });
  }

  function initializeStamped() {
    if (stampedInitialized || !window.StampedFn || typeof window.StampedFn.init !== 'function') return;
    window.StampedFn.init({
      apiKey: settings.stampedApiKey,
      sId: settings.stampedStoreId,
    });
    stampedInitialized = true;
  }

  function findRoot(selector, scope) {
    if (!selector) return null;
    if (scope && scope.matches && scope.matches(selector)) return scope;
    if (scope && scope.querySelector) {
      var scopedRoot = scope.querySelector(selector);
      if (scopedRoot) return scopedRoot;
    }
    return document.querySelector(selector);
  }

  function unmountRegistration(record) {
    if (!record) return;
    record.generation += 1;
    if (record.stopVisibility) record.stopVisibility();
    if (typeof record.cleanup === 'function') {
      try {
        record.cleanup();
      } catch (error) {
        reportError('Section cleanup failed:', error);
      }
    }
    record.root = null;
    record.cleanup = null;
    record.stopVisibility = null;
    record.initialized = false;
  }

  function mountRegistration(record, scope) {
    var root = findRoot(record.options.selector, scope);
    if (!root) return;
    if (record.root === root) return;

    unmountRegistration(record);
    record.root = root;
    var generation = record.generation;

    record.stopVisibility = whenVisible(
      root,
      function () {
        if (record.initialized || record.root !== root) return;
        record.initialized = true;

        return Promise.resolve(record.options.init(root, api)).then(function (cleanup) {
          if (record.generation !== generation || record.root !== root) {
            if (typeof cleanup === 'function') cleanup();
            return;
          }
          if (typeof cleanup === 'function') record.cleanup = cleanup;
        });
      },
      {
        rootMargin: record.options.rootMargin,
        threshold: record.options.threshold,
        immediate: record.options.immediate,
        fallbackTimeout: record.options.fallbackTimeout,
      }
    );
  }

  function registerSection(key, options) {
    if (!key || !options || !options.selector || typeof options.init !== 'function') return noop;

    if (registrations[key]) unmountRegistration(registrations[key]);
    registrations[key] = {
      key: key,
      options: options,
      root: null,
      cleanup: null,
      stopVisibility: null,
      initialized: false,
      generation: 0,
    };
    mountRegistration(registrations[key], document);

    return function () {
      unmountRegistration(registrations[key]);
      delete registrations[key];
    };
  }

  function handleSectionLoad(event) {
    Object.keys(registrations).forEach(function (key) {
      mountRegistration(registrations[key], event.target);
    });
  }

  function handleSectionUnload(event) {
    Object.keys(registrations).forEach(function (key) {
      var record = registrations[key];
      if (record.root && (record.root === event.target || event.target.contains(record.root))) {
        unmountRegistration(record);
      }
    });
  }

  document.addEventListener('shopify:section:load', handleSectionLoad);
  document.addEventListener('shopify:section:unload', handleSectionUnload);
  document.addEventListener('shopify:section:reorder', function () {
    Object.keys(registrations).forEach(function (key) {
      mountRegistration(registrations[key], document);
    });
  });

  var api = {
    designMode: settings.designMode,
    whenVisible: whenVisible,
    loadScriptOnce: loadScriptOnce,
    loadStyleOnce: loadStyleOnce,
    loadSwiper: loadSwiper,
    loadStamped: loadStamped,
    onIdle: onIdle,
    registerSection: registerSection,
  };

  window.TalonHomepagePerformance = api;

  var queuedRegistrations = window.TalonHomepagePerformanceQueue || [];
  window.TalonHomepagePerformanceQueue = [];
  queuedRegistrations.forEach(function (register) {
    try {
      register(api);
    } catch (error) {
      reportError('Queued registration failed:', error);
    }
  });

  document.dispatchEvent(new CustomEvent('talon:homepage-performance:ready'));
})(window, document);
